import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  BarChart3, DollarSign, LayoutDashboard, LayoutGrid, MapPin,
  Settings, ShoppingBag, TrendingUp, Users, Wallet,
  ChevronLeft, ChevronRight, LogOut, Search, HeartHandshake,
} from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { useCompany } from '@/hooks/useCompany'
import { cn } from '@/lib/utils'

export const NAV_ITEMS = [
  { path: '/',            label: 'Dashboard',    icon: LayoutDashboard },
  { path: '/stats',       label: 'Estadísticas', icon: BarChart3 },
  { path: '/productos',   label: 'Productos',    icon: ShoppingBag },
  { path: '/buscador',    label: 'Buscador',     icon: Search },
  { path: '/ayudaventas', label: 'Ayudaventas',  icon: HeartHandshake },
  { path: '/indicadores', label: 'Indicadores',  icon: TrendingUp },
  { path: '/costos',      label: 'Costos',       icon: DollarSign },
  { path: '/origen',      label: 'Origen',       icon: MapPin },
  { path: '/vendedores',  label: 'Vendedores',   icon: Users },
  { path: '/resumen',     label: 'Resumen',      icon: LayoutGrid },
  { path: '/finanzas',    label: 'Finanzas',     icon: Wallet },
  { path: '/config',      label: 'Config',       icon: Settings },
]

export function useSidebarCollapsed() {
  const [collapsed, setCollapsed] = useState(() =>
    localStorage.getItem('sidebarCollapsed') === 'true'
  )
  useEffect(() => {
    const handler = () => setCollapsed(localStorage.getItem('sidebarCollapsed') === 'true')
    window.addEventListener('sidebar-toggle', handler)
    return () => window.removeEventListener('sidebar-toggle', handler)
  }, [])
  return collapsed
}

export function Sidebar() {
  const [collapsed, setCollapsed] = useState(() =>
    localStorage.getItem('sidebarCollapsed') === 'true'
  )
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const { signOut } = useAuth()
  const company = useCompany()

  const toggle = () => {
    setCollapsed(v => {
      const next = !v
      localStorage.setItem('sidebarCollapsed', String(next))
      window.dispatchEvent(new Event('sidebar-toggle'))
      return next
    })
  }

  const handleSignOut = async () => {
    try { await signOut() } catch {}
    navigate('/login')
  }

  return (
    <aside
      className={cn(
        'hidden md:flex flex-col shrink-0 h-screen sticky top-0',
        'bg-gray-950 border-r border-white/8',
        'transition-all duration-300 ease-in-out overflow-hidden',
        collapsed ? 'w-[68px]' : 'w-[224px]'
      )}
    >
      {/* Logo */}
      <div className={cn(
        'flex items-center h-16 px-3 border-b border-white/8 shrink-0 gap-3',
        collapsed && 'justify-center'
      )}>
        <img
          src={company.logoUrl ?? '/LogoNibpr.png'}
          alt={company.shortName}
          className="h-8 w-8 shrink-0 rounded object-contain"
        />
        {!collapsed && (
          <div className="min-w-0">
            <p className="text-white font-bold text-sm leading-tight truncate">{company.shortName}</p>
            <p className="text-white/40 text-xs leading-tight truncate">Analytics</p>
          </div>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 py-3 overflow-y-auto overflow-x-hidden space-y-0.5 px-2">
        {NAV_ITEMS.map(({ path, label, icon: Icon }) => {
          const isActive = path === '/' ? pathname === '/' : pathname.startsWith(path)
          return (
            <button
              key={path}
              onClick={() => navigate(path)}
              title={collapsed ? label : undefined}
              className={cn(
                'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-sm font-medium',
                collapsed ? 'justify-center' : '',
                isActive
                  ? 'bg-blue-600/20 text-blue-400 border border-blue-500/25'
                  : 'text-white/50 hover:text-white hover:bg-white/8'
              )}
            >
              <Icon className="h-[18px] w-[18px] shrink-0" />
              {!collapsed && <span className="truncate">{label}</span>}
            </button>
          )
        })}
      </nav>

      {/* Bottom */}
      <div className="border-t border-white/8 p-2 space-y-1 shrink-0">
        <button
          onClick={handleSignOut}
          title={collapsed ? 'Cerrar sesión' : undefined}
          className={cn(
            'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-sm',
            'text-red-400/50 hover:text-red-400 hover:bg-red-500/10',
            collapsed && 'justify-center'
          )}
        >
          <LogOut className="h-4 w-4 shrink-0" />
          {!collapsed && <span>Cerrar sesión</span>}
        </button>
        <button
          onClick={toggle}
          title={collapsed ? 'Expandir' : 'Colapsar'}
          className={cn(
            'w-full flex items-center gap-2 px-3 py-2 rounded-lg transition-colors text-xs',
            'text-white/25 hover:text-white/50 hover:bg-white/5',
            collapsed && 'justify-center'
          )}
        >
          {collapsed ? <ChevronRight className="h-3.5 w-3.5" /> : (
            <><ChevronLeft className="h-3.5 w-3.5" /><span>Colapsar</span></>
          )}
        </button>
      </div>
    </aside>
  )
}
